export class ProtocolError extends Error {
  constructor(message: string, readonly code = 'PROTOCOL_INVALID') {
    super(message);
    this.name = 'ProtocolError';
  }
}

export function generateSpaceKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

export async function encryptAesGcm(
  plain: Uint8Array,
  rawKey: Uint8Array,
  iv = crypto.getRandomValues(new Uint8Array(12)),
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', asBuffer(rawKey), 'AES-GCM', false, ['encrypt']);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asBuffer(iv) }, key, asBuffer(plain));
  const out = new Uint8Array(iv.length + encrypted.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(encrypted), iv.length);
  return out;
}

export async function decryptAesGcm(cipher: Uint8Array, rawKey: Uint8Array): Promise<Uint8Array> {
  if (cipher.length < 28) throw damagedLinkError();
  try {
    const key = await crypto.subtle.importKey('raw', asBuffer(rawKey), 'AES-GCM', false, ['decrypt']);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: asBuffer(cipher.subarray(0, 12)) },
      key,
      asBuffer(cipher.subarray(12)),
    );
    return new Uint8Array(plain);
  } catch (error) {
    if (error instanceof DOMException && (error.name === 'OperationError' || error.name === 'DataError')) {
      throw damagedLinkError();
    }
    throw error;
  }
}

export async function deriveBackupKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    asBuffer(new TextEncoder().encode(passphrase)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: asBuffer(salt), iterations: 210_000 },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function damagedLinkError(): ProtocolError {
  return new ProtocolError('链接内容已损坏或密钥不匹配', 'CRYPTO_INTEGRITY');
}

function asBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
